import { DashboardRequest } from '@/types/action-plan/DashboardRequest';
import { DuAnDetailView } from '@/types/action-plan/DuAnDetailView';
import { CloneDuAnRequest } from '@/types/action-plan/CloneDuAnRequest';
import { RequestSDK } from '@/types/sdk/requestSDK';
import { ApiResponse, PagedApiResponse } from '@/types/base';
import request from '../base-service';
import { InternalAxiosRequestConfig } from 'axios';
import type { ActionPlanApiRequestOptions } from '@/configs/actionPlanV2ApiOrigin';
import { List } from 'lodash';
import { HistoryItemView } from '@/types/action-plan/ActionPlan/HistoryItemView';
import { GhiChuRequest } from '@/types/action-plan/ActionPlan/GhiChuRequest';
import { DuAnCongViecTree, ItemDashboardDuAn, ViewDashboardDuAn } from '@/types/action-plan/DuAnView';
import { RequestDTO, ImportCongViecRequest } from '@/types/requestDTO';
import { ExportCongViecRequest } from '@/types/action-plan/ExportCongViecRequest';
import { normalizeActionPlanDatesForApi } from '@/utils/dateOnlyForApi';

const BASE_URL = '/v1/duan';

export const actionPlanApi = {
  /**
   * POST: lấy dữ liệu dashboard dự án
   */
  //DashboardController
  getDashboardDuAn: (model: DashboardRequest, options?: ActionPlanApiRequestOptions) => {
    return request.post<ApiResponse<ViewDashboardDuAn>>(`${BASE_URL}/getdashboardduan`, model, {
      ...options,
      authorization: true,
    } as InternalAxiosRequestConfig);
  },

  /**
   * POST: lấy item dự án
   */
  getItemDuAn: (model: DashboardRequest, options?: ActionPlanApiRequestOptions) => {
    return request.post<ApiResponse<DuAnDetailView>>(`${BASE_URL}/getitemduan`, model, {
      ...options,
      authorization: true,
    } as InternalAxiosRequestConfig);
  },

  /**
   * POST: chỉnh sửa dự án
   */
  editDuAn: (model: DuAnDetailView, options?: ActionPlanApiRequestOptions) => {
    return request.post<ApiResponse<boolean>>(
      `${BASE_URL}/editduan`,
      normalizeActionPlanDatesForApi(model) as DuAnDetailView,
      {
        ...options,
        authorization: true,
      } as InternalAxiosRequestConfig
    );
  },

  /**
   * POST: xóa dự án
   */
  deleteDuAn: (model: RequestSDK) => {
    return request.post<ApiResponse<boolean>>(`${BASE_URL}/deleteduan`, model, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },
  /**
   * POST: TEST ghichu API, chưa đúng
   */
  history: (model: GhiChuRequest) => {
    return request.post<ApiResponse<List<HistoryItemView>>>(`${BASE_URL}/ghichu/history`, model, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },

  /**
   * POST: lấy danh sách việc trong ngày (pageCurrent > 0 → phân trang, có pagination).
   */
  getViecTrongNgay: (model: DashboardRequest) => {
    return request.post<PagedApiResponse<DuAnCongViecTree>>(
      `${BASE_URL}/getviectrongngay`,
      model,
      {
        authorization: true,
      } as InternalAxiosRequestConfig
    );
  },
  getBaoCaoNhanhViecTrongNgay: (model: DashboardRequest, options?: ActionPlanApiRequestOptions) => {
    return request.post<ApiResponse<ItemDashboardDuAn>>(
      `${BASE_URL}/baocaonhanhcongviecduan`,
      model,
      {
        ...options,
        authorization: true,
      } as InternalAxiosRequestConfig
    );
  },
  /** POST: lấy dữ liệu form Clone (sao chép/chuyển). Body: DashboardRequest (idCongViec = id dự án gốc). */
  getitemcloneduan: (model: DashboardRequest) => {
    return request.post<ApiResponse<DuAnDetailView>>(`${BASE_URL}/getitemcloneduan`, model, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },
  /** POST: thực hiện sao chép hoặc chuyển dự án. Body: CloneDuAnRequest. */
  cloneDuAn: (model: CloneDuAnRequest) => {
    return request.post<ApiResponse<boolean>>(`${BASE_URL}/cloneDuAn`, model, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },
  /** POST: load danh sách mục tiêu theo phụ trách (IDChoice, Year) */
  loadMucTieu: (
    model: { idChoice: string; year: number; search?: string; phanHe: string },
    options?: ActionPlanApiRequestOptions
  ) => {
    return request.post<ApiResponse<{ phuTrachMucTieu: string; mucTieus: { id: string; text: string }[] }>>(
      `${BASE_URL}/loadmuctieu`,
      {
        IDChoice: model.idChoice,
        Year: model.year,
        Search: model.search,
        PhanHe: model.phanHe,
      },
      {
        ...options,
        authorization: true,
      } as InternalAxiosRequestConfig
    );
  },
  export: (requestDTO: ExportCongViecRequest) => {
    return request.post(`${BASE_URL}/exportcongviec`, requestDTO || {}, {
      responseType: 'blob',
      authorization: true,
      headers: {
        'Content-Type': 'application/json',
      },
    } as InternalAxiosRequestConfig);
  },
  exportTemplate: (requestDTO: RequestDTO) => {
    return request.post(`${BASE_URL}/exporttemplate`, requestDTO || {}, {
      responseType: 'blob',
      authorization: true,
      headers: {
        'Content-Type': 'application/json',
      },
    } as InternalAxiosRequestConfig);
  },
  /** POST: import công việc. [FromForm] ImportCongViecRequest. */
  import: (model: ImportCongViecRequest) => {
    const formData = new FormData();
    formData.append('PhanHe', model.PhanHe ?? '');
    formData.append('IDBoPhan', String(model.IDBoPhan ?? 0));
    formData.append('IDMucTieu', model.IDMucTieu ?? '');
    formData.append('IDDuAn', model.IDDuAn ?? '');
    formData.append('SelectBophan', model.SelectBophan ?? '');
    formData.append('Year', String(model.Year ?? new Date().getFullYear()));
    if (model.File) formData.append('File', model.File);
    return request.post<ApiResponse<Blob>>(`${BASE_URL}/importcongviec`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      authorization: true,
    } as InternalAxiosRequestConfig);
  },
  exportbaocaonhanh: (requestDTO: DashboardRequest) => {
    return request.post(`${BASE_URL}/exportbaocaonhanh`, requestDTO || {}, {
      responseType: 'blob',
      authorization: true,
      headers: {
        'Content-Type': 'application/json',
      },
    } as InternalAxiosRequestConfig);
  },


  /**
   * POST: Toggle hiển thị trong trang Quản lý Action Plan Bộ phận
   */
  toggleHienThiTrangQuanLy: (model: { id: string; hienThiTrangQuanLy: boolean }) => {
    return request.post<ApiResponse<boolean>>(`${BASE_URL}/togglehienthitrangquanly`, model, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },

};
